/**
 * Client for POST /api/cognitory/v2/provisioning-setup/activate (owner initial activation).
 */

import {
  COGNITORY_V2_PROVISIONING_PRODUCT_INITIAL_ACTIVATION_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import { parseNonemptyRequestId } from "./cognitoryProvisioningLibraryStateClient";

export type ProvisioningProductInitialActivationSuccessDecision =
  | "activation_completed"
  | "activation_already_exact";

export type ProvisioningProductInitialActivationClientResult =
  | {
      kind: "succeeded";
      requestId: string;
      decision: ProvisioningProductInitialActivationSuccessDecision;
    }
  | {
      kind:
        | "not_ready"
        | "state_conflict"
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

function hasExactKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const keys = Object.keys(record);
  if (keys.length !== allowed.length) {
    return false;
  }
  for (const key of allowed) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      return false;
    }
  }
  return true;
}

type ActivationDenialKind =
  | "unauthenticated"
  | "forbidden"
  | "invalid_request"
  | "temporary_failure";

function parseDenial(
  status: number,
  body: unknown,
  expectedDecision: ActivationDenialKind,
  expectedStatus: number,
): { kind: ActivationDenialKind; requestId: string } | null {
  if (status !== expectedStatus || !body || typeof body !== "object") {
    return null;
  }
  const record = body as Record<string, unknown>;
  if (!hasExactKeys(record, ["decision", "requestId"])) {
    return null;
  }
  if (record.decision !== expectedDecision) {
    return null;
  }
  const requestId = parseNonemptyRequestId(record.requestId);
  if (!requestId) {
    return null;
  }
  return { kind: expectedDecision, requestId };
}

function parseConflict(
  status: number,
  body: unknown,
  expectedDecision: "activation_not_ready" | "activation_state_conflict",
): { kind: "not_ready" | "state_conflict"; requestId: string } | null {
  if (status !== 409 || !body || typeof body !== "object") {
    return null;
  }
  const record = body as Record<string, unknown>;
  if (!hasExactKeys(record, ["decision", "requestId"])) {
    return null;
  }
  if (record.decision !== expectedDecision) {
    return null;
  }
  const requestId = parseNonemptyRequestId(record.requestId);
  if (!requestId) {
    return null;
  }
  return {
    kind:
      expectedDecision === "activation_not_ready"
        ? "not_ready"
        : "state_conflict",
    requestId,
  };
}

async function postProvisioningProductInitialActivation(
  cognitoryId: string,
  input?: {
    apiBaseUrl?: string;
    bearerToken?: string | null;
    fetchImpl?: typeof fetch;
  },
): Promise<{ status: number; body: unknown }> {
  const fetchImpl = input?.fetchImpl ?? fetch;
  const token =
    input?.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { status: 401, body: null };
  }

  const base = input?.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  try {
    const response = await fetchImpl(
      `${base}${COGNITORY_V2_PROVISIONING_PRODUCT_INITIAL_ACTIVATION_PATH}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        cache: "no-store",
        body: JSON.stringify({ cognitoryId }),
      },
    );
    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
    return { status: response.status, body: parsed };
  } catch {
    return { status: 503, body: null };
  }
}

export async function executeProvisioningProductInitialActivation(input: {
  cognitoryId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<ProvisioningProductInitialActivationClientResult> {
  const { status, body } = await postProvisioningProductInitialActivation(
    input.cognitoryId,
    input,
  );

  if (status === 401) {
    const denial = parseDenial(status, body, "unauthenticated", 401);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 403) {
    const denial = parseDenial(status, body, "forbidden", 403);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 400) {
    const denial = parseDenial(status, body, "invalid_request", 400);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 503) {
    const denial = parseDenial(status, body, "temporary_failure", 503);
    return denial ?? { kind: "unable_to_verify", requestId: null };
  }
  if (status === 409) {
    const notReady = parseConflict(status, body, "activation_not_ready");
    if (notReady) {
      return notReady;
    }
    const conflict = parseConflict(status, body, "activation_state_conflict");
    if (conflict) {
      return conflict;
    }
    return { kind: "unable_to_verify", requestId: null };
  }
  if (status === 200) {
    if (!body || typeof body !== "object") {
      return { kind: "unable_to_verify", requestId: null };
    }
    const record = body as Record<string, unknown>;
    if (!hasExactKeys(record, ["decision", "requestId"])) {
      return { kind: "unable_to_verify", requestId: null };
    }
    const requestId = parseNonemptyRequestId(record.requestId);
    if (!requestId) {
      return { kind: "unable_to_verify", requestId: null };
    }
    if (record.decision === "activation_completed") {
      return { kind: "succeeded", requestId, decision: "activation_completed" };
    }
    if (record.decision === "activation_already_exact") {
      return {
        kind: "succeeded",
        requestId,
        decision: "activation_already_exact",
      };
    }
    return { kind: "unable_to_verify", requestId: null };
  }

  return { kind: "unable_to_verify", requestId: null };
}
